import { useState, useEffect } from 'react';
import { useChainId, useAccount } from 'wagmi';
import { toast } from 'sonner';
import { Settings, Save, RotateCcw, Users, Plus, Trash2, RefreshCw, Shield, Calendar, Coins, Lock, Pencil } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Skeleton } from '@/shared/ui/skeleton';
import { Badge } from '@/shared/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { contracts, galactica, galacticaCassiopeia, ADMIN_ADDRESSES } from '@/shared/config';
import { orpc } from '@/shared/api/client';
import type { ChainConfig, EmissionEpoch } from '@/shared/api/db-d1';
import { DEFAULT_VESTING_START, DEFAULT_VESTING_END, DEFAULT_EMISSION_EPOCHS, getCurrentEpoch } from '@/shared/hooks/useContractSettings';

interface Admin {
  id: number;
  walletAddress: string;
  addedBy: string | null;
  createdAt: number;
}

interface ContractSettings {
  rewardDistributor: string;
  gnetRewardDistributor: string;
  gubi: string;
  votingEscrow: string;
  indexPool: string;
  config: ChainConfig;
}

const getDefaultSettings = (chainId: number): ContractSettings => ({
  rewardDistributor: '',
  gnetRewardDistributor: '',
  gubi: contracts.gubi[chainId as keyof typeof contracts.gubi] || '',
  votingEscrow: contracts.votingEscrow[chainId as keyof typeof contracts.votingEscrow] || '',
  indexPool: contracts.indexPool[chainId as keyof typeof contracts.indexPool] || '',
  config: {},
});

// Helper to convert timestamp to date input value (YYYY-MM-DD)
const timestampToDateInput = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0];
};

// Helper to convert date input value to timestamp
const dateInputToTimestamp = (dateStr: string): number => {
  return new Date(dateStr + 'T00:00:00Z').getTime();
};

export function SettingsPage() {
  const chainId = useChainId();
  const { address: walletAddress } = useAccount();
  const chainName = chainId === galactica.id ? 'Galactica Mainnet' : 'Cassiopeia Testnet';

  const [settings, setSettings] = useState<ContractSettings>(getDefaultSettings(chainId));
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Admin management state
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [newAdminAddress, setNewAdminAddress] = useState('');
  const [isAddingAdmin, setIsAddingAdmin] = useState(false);
  const [removingAdminId, setRemovingAdminId] = useState<number | null>(null);

  // Epoch editing state
  const [editingEpochIndex, setEditingEpochIndex] = useState<number | null>(null);
  const [editingEpochData, setEditingEpochData] = useState<{ startDate: string; annualEmission: string }>({ startDate: '', annualEmission: '' });
  const [newEpochData, setNewEpochData] = useState<{ startDate: string; annualEmission: string }>({ startDate: '', annualEmission: '' });

  // Fetch settings from database
  const fetchSettings = async () => {
    setSettingsLoading(true);
    try {
      const result = await orpc.settings.get({ chainId });
      if (result) {
        setSettings({
          rewardDistributor: result.rewardDistributor,
          gnetRewardDistributor: result.gnetRewardDistributor,
          gubi: result.gubi,
          votingEscrow: result.votingEscrow,
          indexPool: result.indexPool,
          config: result.config || {},
        });
      } else {
        setSettings(getDefaultSettings(chainId));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
      setSettings(getDefaultSettings(chainId));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [chainId]);

  // Fetch admins on mount
  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setAdminsLoading(true);
    try {
      const result = await orpc.admins.list({});
      setAdmins(result.admins);
    } catch (err) {
      console.error('Failed to fetch admins:', err);
      toast.error('Failed to load admins');
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminAddress || !/^0x[a-fA-F0-9]{40}$/.test(newAdminAddress)) {
      toast.error('Invalid wallet address');
      return;
    }

    // Check if already a hardcoded admin
    if (ADMIN_ADDRESSES.some(a => a.toLowerCase() === newAdminAddress.toLowerCase())) {
      toast.error('Address is already a system admin');
      return;
    }

    // Check if already exists in D1
    if (admins.some(a => a.walletAddress.toLowerCase() === newAdminAddress.toLowerCase())) {
      toast.error('Address is already an admin');
      return;
    }

    setIsAddingAdmin(true);
    try {
      await orpc.admins.add({
        wallet: newAdminAddress,
        addedBy: walletAddress,
      });
      toast.success('Admin added');
      setNewAdminAddress('');
      fetchAdmins();
    } catch (err) {
      console.error('Failed to add admin:', err);
      toast.error('Failed to add admin');
    } finally {
      setIsAddingAdmin(false);
    }
  };

  const handleRemoveAdmin = async (admin: Admin) => {
    // Prevent removing the last admin
    if (admins.length <= 1) {
      toast.error('Cannot remove the last admin');
      return;
    }

    setRemovingAdminId(admin.id);
    try {
      await orpc.admins.remove({ wallet: admin.walletAddress });
      toast.success('Admin removed');
      fetchAdmins();
    } catch (err) {
      console.error('Failed to remove admin:', err);
      toast.error('Failed to remove admin');
    } finally {
      setRemovingAdminId(null);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await orpc.settings.save({ chainId, settings });
      toast.success('Settings saved');
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(getDefaultSettings(chainId));
    toast.info('Reset to defaults (not saved yet)');
  };

  const isValidAddress = (addr: string) => !addr || /^0x[a-fA-F0-9]{40}$/.test(addr);

  const truncateAddress = (addr: string) => `${addr.slice(0, 6)}...${addr.slice(-4)}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2b2b2b]">Settings</h1>
        <p className="text-[#807f7e]">Configure contract addresses and admin access</p>
      </div>

      {/* Admin Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Admin Addresses
              </CardTitle>
              <CardDescription>
                Wallet addresses with admin access to this panel
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAdmins} disabled={adminsLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${adminsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add new admin */}
          <div className="flex gap-2">
            <Input
              placeholder="0x... wallet address"
              value={newAdminAddress}
              onChange={(e) => setNewAdminAddress(e.target.value)}
              className={`font-mono ${newAdminAddress && !isValidAddress(newAdminAddress) ? 'border-red-500' : ''}`}
            />
            <Button
              onClick={handleAddAdmin}
              disabled={isAddingAdmin || !newAdminAddress || !isValidAddress(newAdminAddress)}
            >
              {isAddingAdmin ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add Admin
            </Button>
          </div>

          {/* Admin list */}
          <div className="border rounded-lg divide-y">
            {/* Hardcoded system admins - always shown, cannot be removed */}
            {ADMIN_ADDRESSES.map((address) => (
              <div key={address} className="flex items-center justify-between p-3 bg-gray-50">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-sm">{address}</code>
                    <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                      <Shield className="w-3 h-3 mr-1" />
                      System
                    </Badge>
                  </div>
                  <span className="text-xs text-[#807f7e]">
                    Hardcoded admin (cannot be removed)
                  </span>
                </div>
              </div>
            ))}

            {/* D1 admins - can be removed */}
            {adminsLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              admins
                .filter((admin) => !ADMIN_ADDRESSES.some(
                  (addr) => addr.toLowerCase() === admin.walletAddress.toLowerCase()
                ))
                .map((admin) => (
                  <div key={admin.id} className="flex items-center justify-between p-3">
                    <div className="flex flex-col">
                      <code className="font-mono text-sm">{admin.walletAddress}</code>
                      <span className="text-xs text-[#807f7e]">
                        Added {new Date(admin.createdAt * 1000).toLocaleDateString()}
                        {admin.addedBy && ` by ${truncateAddress(admin.addedBy)}`}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAdmin(admin)}
                      disabled={removingAdminId === admin.id}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      {removingAdminId === admin.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))
            )}
          </div>

          <p className="text-xs text-[#807f7e]">
            System admins are hardcoded and cannot be removed. Additional admins added here are stored in the database.
          </p>
        </CardContent>
      </Card>

      {/* Contract Addresses */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Contract Addresses
              </CardTitle>
              <CardDescription>
                Chain ID: {chainId} ({chainName})
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={settingsLoading}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving || settingsLoading}>
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <>
          <div className="space-y-2">
            <Label htmlFor="rewardDistributor">gUBI Reward Distributor</Label>
            <Input
              id="rewardDistributor"
              placeholder="0x..."
              value={settings.rewardDistributor}
              onChange={(e) => setSettings({ ...settings, rewardDistributor: e.target.value })}
              className={!isValidAddress(settings.rewardDistributor) ? 'border-red-500' : ''}
            />
            <p className="text-xs text-[#807f7e]">
              Merkle-based gUBI reward distribution contract. Used for UBI rewards.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gnetRewardDistributor">GNET Reward Distributor</Label>
            <Input
              id="gnetRewardDistributor"
              placeholder="0x..."
              value={settings.gnetRewardDistributor}
              onChange={(e) => setSettings({ ...settings, gnetRewardDistributor: e.target.value })}
              className={!isValidAddress(settings.gnetRewardDistributor) ? 'border-red-500' : ''}
            />
            <p className="text-xs text-[#807f7e]">
              Merkle-based GNET vesting distribution contract. Used for investor vesting claims.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="gubi">gUBI Token</Label>
            <Input
              id="gubi"
              placeholder="0x..."
              value={settings.gubi}
              onChange={(e) => setSettings({ ...settings, gubi: e.target.value })}
              className={!isValidAddress(settings.gubi) ? 'border-red-500' : ''}
            />
            <p className="text-xs text-[#807f7e]">
              ERC-20 token distributed as Universal Basic Income rewards.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="votingEscrow">Voting Escrow</Label>
            <Input
              id="votingEscrow"
              placeholder="0x..."
              value={settings.votingEscrow}
              onChange={(e) => setSettings({ ...settings, votingEscrow: e.target.value })}
              className={!isValidAddress(settings.votingEscrow) ? 'border-red-500' : ''}
            />
            <p className="text-xs text-[#807f7e]">
              veGNET contract for time-locked GNET staking and governance voting power.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="indexPool">Index Pool</Label>
            <Input
              id="indexPool"
              placeholder="0x..."
              value={settings.indexPool}
              onChange={(e) => setSettings({ ...settings, indexPool: e.target.value })}
              className={!isValidAddress(settings.indexPool) ? 'border-red-500' : ''}
            />
            <p className="text-xs text-[#807f7e]">
              Liquidity pool contract for token swaps and liquidity provision.
            </p>
          </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* GNET Vesting Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                GNET Vesting Schedule
              </CardTitle>
              <CardDescription>
                Configure vesting dates for this chain. Leave blank to use production defaults.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleReset} disabled={settingsLoading}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving || settingsLoading}>
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="vestingStart">Vesting Start Date</Label>
                  <Input
                    id="vestingStart"
                    type="date"
                    value={timestampToDateInput(settings.config.gnetVestingStart ?? DEFAULT_VESTING_START)}
                    onChange={(e) => setSettings({
                      ...settings,
                      config: {
                        ...settings.config,
                        gnetVestingStart: e.target.value ? dateInputToTimestamp(e.target.value) : undefined,
                      },
                    })}
                  />
                  <p className="text-xs text-[#807f7e]">
                    Default: Jan 3, 2026
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vestingEnd">Vesting End Date</Label>
                  <Input
                    id="vestingEnd"
                    type="date"
                    value={timestampToDateInput(settings.config.gnetVestingEnd ?? DEFAULT_VESTING_END)}
                    onChange={(e) => setSettings({
                      ...settings,
                      config: {
                        ...settings.config,
                        gnetVestingEnd: e.target.value ? dateInputToTimestamp(e.target.value) : undefined,
                      },
                    })}
                  />
                  <p className="text-xs text-[#807f7e]">
                    Default: Jan 3, 2028 (24 months)
                  </p>
                </div>
              </div>
              <p className="text-xs text-[#807f7e] bg-blue-50 p-3 rounded-lg">
                For testnet (Cassiopeia), you can set past dates to test vesting distribution immediately.
                For mainnet, use the production schedule (Jan 2026 - Jan 2028).
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* gUBI Emission Epochs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                gUBI Emission Epochs
              </CardTitle>
              <CardDescription>
                Configure emission rates for different periods. Each epoch continues until the next one starts.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={isSaving || settingsLoading}>
                {isSaving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {settingsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <>
              {/* Epochs table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Start Date</TableHead>
                      <TableHead className="text-right">Annual Rate (gUBI)</TableHead>
                      <TableHead className="text-right">Daily Rate</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(settings.config.emissionEpochs ?? (DEFAULT_EMISSION_EPOCHS[chainId] || []))
                      .sort((a, b) => a.startDate - b.startDate)
                      .map((epoch, index) => {
                        const isPast = epoch.startDate <= Date.now();
                        const isEditing = editingEpochIndex === index;
                        const dailyRate = epoch.annualEmission / 365;
                        const currentEpoch = getCurrentEpoch(settings.config.emissionEpochs ?? (DEFAULT_EMISSION_EPOCHS[chainId] || []));
                        const isCurrent = currentEpoch && currentEpoch.startDate === epoch.startDate;

                        if (isEditing) {
                          return (
                            <TableRow key={epoch.startDate}>
                              <TableCell>
                                <Input
                                  type="date"
                                  value={editingEpochData.startDate}
                                  onChange={(e) => setEditingEpochData({ ...editingEpochData, startDate: e.target.value })}
                                  className="w-40"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={editingEpochData.annualEmission}
                                  onChange={(e) => setEditingEpochData({ ...editingEpochData, annualEmission: e.target.value })}
                                  className="w-32 text-right"
                                  placeholder="Annual gUBI"
                                />
                              </TableCell>
                              <TableCell className="text-right text-[#807f7e]">
                                ~{(Number(editingEpochData.annualEmission) / 365).toLocaleString(undefined, { maximumFractionDigits: 0 })}/day
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const epochs = [...(settings.config.emissionEpochs ?? (DEFAULT_EMISSION_EPOCHS[chainId] || []))];
                                      const sortedEpochs = epochs.sort((a, b) => a.startDate - b.startDate);
                                      sortedEpochs[index] = {
                                        startDate: dateInputToTimestamp(editingEpochData.startDate),
                                        annualEmission: Number(editingEpochData.annualEmission),
                                      };
                                      setSettings({
                                        ...settings,
                                        config: { ...settings.config, emissionEpochs: sortedEpochs },
                                      });
                                      setEditingEpochIndex(null);
                                      toast.success('Epoch updated (save to persist)');
                                    }}
                                    className="text-green-600 hover:text-green-700"
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingEpochIndex(null)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        return (
                          <TableRow key={epoch.startDate} className={isCurrent ? 'bg-green-50' : ''}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {new Date(epoch.startDate).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}
                                {isCurrent && (
                                  <Badge variant="outline" className="bg-green-100 text-green-700 text-xs">
                                    Current
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {epoch.annualEmission.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right text-[#807f7e]">
                              ~{dailyRate.toLocaleString(undefined, { maximumFractionDigits: 0 })}/day
                            </TableCell>
                            <TableCell>
                              {isPast ? (
                                <div className="flex items-center gap-1 text-[#807f7e]">
                                  <Lock className="w-4 h-4" />
                                  <span className="text-xs">Locked</span>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      setEditingEpochIndex(index);
                                      setEditingEpochData({
                                        startDate: timestampToDateInput(epoch.startDate),
                                        annualEmission: String(epoch.annualEmission),
                                      });
                                    }}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => {
                                      const epochs = [...(settings.config.emissionEpochs ?? (DEFAULT_EMISSION_EPOCHS[chainId] || []))]
                                        .sort((a, b) => a.startDate - b.startDate)
                                        .filter((_, i) => i !== index);
                                      setSettings({
                                        ...settings,
                                        config: { ...settings.config, emissionEpochs: epochs },
                                      });
                                      toast.success('Epoch removed (save to persist)');
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>

              {/* Add new epoch */}
              <div className="border rounded-lg p-4 bg-gray-50">
                <Label className="text-sm font-medium mb-3 block">Add New Epoch</Label>
                <div className="flex gap-3 items-end">
                  <div className="space-y-1">
                    <Label htmlFor="newEpochDate" className="text-xs text-[#807f7e]">Start Date</Label>
                    <Input
                      id="newEpochDate"
                      type="date"
                      value={newEpochData.startDate}
                      onChange={(e) => setNewEpochData({ ...newEpochData, startDate: e.target.value })}
                      className="w-40"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="newEpochRate" className="text-xs text-[#807f7e]">Annual Emission (gUBI)</Label>
                    <Input
                      id="newEpochRate"
                      type="number"
                      value={newEpochData.annualEmission}
                      onChange={(e) => setNewEpochData({ ...newEpochData, annualEmission: e.target.value })}
                      className="w-40"
                      placeholder="e.g. 9030820"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      if (!newEpochData.startDate || !newEpochData.annualEmission) {
                        toast.error('Please fill in both fields');
                        return;
                      }
                      const newEpoch: EmissionEpoch = {
                        startDate: dateInputToTimestamp(newEpochData.startDate),
                        annualEmission: Number(newEpochData.annualEmission),
                      };
                      const epochs = [...(settings.config.emissionEpochs ?? (DEFAULT_EMISSION_EPOCHS[chainId] || [])), newEpoch];
                      setSettings({
                        ...settings,
                        config: { ...settings.config, emissionEpochs: epochs },
                      });
                      setNewEpochData({ startDate: '', annualEmission: '' });
                      toast.success('Epoch added (save to persist)');
                    }}
                    disabled={!newEpochData.startDate || !newEpochData.annualEmission}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Epoch
                  </Button>
                </div>
              </div>

              <p className="text-xs text-[#807f7e] bg-blue-50 p-3 rounded-lg">
                Past epochs (start date before today) are locked and cannot be modified to preserve audit trail.
                The current epoch is highlighted in green.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
